/**
 * src/components/ejecuciones/EstadoPlantillas.jsx
 * ¿Qué tenants llevan ya la plantilla vigente del dashboard?
 *
 * Nace de un fallo concreto (31-08-2026): Manuel regeneró los nueve tenants y no
 * vio ninguno de los cambios, porque el código estaba en la rama y no en `main`.
 * La pregunta que lo habría resuelto en diez segundos —«¿le llegó la plantilla
 * nueva a este tenant?»— tenía respuesta desde el Lote 6, pero solo por API con
 * `X-Admin-Token`: o sea, para nadie. Regenerar y comprobar lo regenerado se
 * hacen en el mismo sitio, así que el sitio de esto es Ejecuciones.
 *
 * ⚠️ Lo que este bloque NO hace, a propósito: regenerar. La regeneración ya vive
 * en la ficha del cliente, y ponerla también aquí sería el segundo botón que
 * hace lo mismo — la clase de duplicado que el rediseño del panel vino a quitar.
 * Aquí se DIAGNOSTICA; se actúa donde ya se actuaba.
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Pildora } from '../ui/Pildora'
import { listarEstadoDashboards } from '../../api/dashboardsEstado'

const FILTROS = [
  { clave: 'todos', etiqueta: 'Todos' },
  { clave: 'faltan', etiqueta: 'Les falta algo' },
  { clave: 'al_dia', etiqueta: 'Al día' },
]

// Fecha corta y legible. Sin hora: lo que importa aquí es de qué DÍA es el
// horneado, no el minuto.
function _fecha(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function EstadoPlantillas() {
  const navigate = useNavigate()
  const [datos, setDatos] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')
  const [filtro, setFiltro] = useState('todos')
  const [abierto, setAbierto] = useState('')   // cliente_id con el detalle desplegado
  // Desglose del peso, aparte del anterior: son dos preguntas distintas («qué le
  // falta» y «de qué está hecho su peso») y compartir el estado obligaría a cerrar
  // una para ver la otra.
  const [peso, setPeso] = useState('')

  const cargar = async () => {
    setCargando(true)
    setError('')
    try {
      setDatos(await listarEstadoDashboards())
    } catch (e) {
      // Un error de carga NO puede parecerse a "no falta nada": esta pantalla
      // existe para responder una pregunta, y "no lo sé" es una respuesta
      // legítima que hay que dar en voz alta.
      setError(e?.response?.data?.detail || e?.message || 'No se pudo consultar el estado')
      setDatos(null)
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => { cargar() }, [])

  // La clave es `tenants` — verificada en el `return` del endpoint, no supuesta.
  // Un `a || b || []` de tanteo habría convertido un cambio de contrato en una
  // tabla vacía silenciosa en vez de en un fallo visible.
  const todas = datos?.tenants || []
  const filas = useMemo(() => {
    if (filtro === 'faltan') return todas.filter(f => !f.al_dia)
    if (filtro === 'al_dia') return todas.filter(f => f.al_dia)
    return todas
  }, [todas, filtro])

  // El recuento cuenta SOLO tenants activos. Un tenant de baja sigue teniendo su
  // dashboard servido —por eso aparece en la tabla— pero su plantilla vieja no es
  // trabajo pendiente de nadie, y contarlo hinchaba la cifra que decide si hay que
  // volver a regenerar. Los de baja se dicen aparte, que no es lo mismo que
  // esconderlos.
  const activos = todas.filter(f => f.activo !== false)
  const nFaltan = activos.filter(f => !f.al_dia).length
  const nBajaFaltan = todas.filter(f => f.activo === false && !f.al_dia).length

  return (
    <Card
      title="Plantilla del dashboard por tenant"
      subtitle="Tras desplegar y regenerar, aquí se ve a quién le llegó el código nuevo y a quién no."
      action={
        <Button variant="secondary" size="sm" onClick={cargar} loading={cargando}>
          Refrescar
        </Button>
      }
    >
      <div style={{
        display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap',
        alignItems: 'center', marginBottom: 'var(--space-4)',
      }}>
        {FILTROS.map(f => (
          <Pildora key={f.clave} activa={filtro === f.clave}
                   onClick={() => setFiltro(f.clave)}>
            {f.etiqueta}
          </Pildora>
        ))}
        {todas.length > 0 && (
          <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)',
                         color: nFaltan ? 'var(--color-error)' : 'var(--color-ok)' }}>
            {nFaltan === 0
              ? `los ${activos.length} tenants activos llevan la plantilla vigente`
              : `${nFaltan} de ${activos.length} activos sin la plantilla vigente`}
          </span>
        )}
        {nBajaFaltan > 0 && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
            · {nBajaFaltan} de baja, sin regenerar (no cuentan)
          </span>
        )}
      </div>

      {error && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)',
                      marginBottom: 'var(--space-4)' }}>
          {error}
        </div>
      )}

      {!error && !cargando && todas.length === 0 && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
          Todavía no hay ningún dashboard horneado.
        </div>
      )}

      {filas.length === 0 && todas.length > 0 && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
          Ningún tenant coincide con el filtro.
        </div>
      )}

      {filas.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-3)' }}>
                {['Tenant', 'Plan', 'Plantilla', 'Horneado', 'Peso', 'Qué le falta'].map(r => (
                  <th key={r} style={{ padding: '6px 8px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map(f => {
                const faltan = f.faltan || []
                const desplegado = abierto === f.cliente_id
                // Constantes RETIRADAS del proyecto que este HTML todavía carga:
                // peso muerto y, peor, una segunda copia del catálogo que puede
                // discrepar de la que se enseña. Se avisa en la propia cifra.
                const muerto = (f.peso?.muerto?.kb || 0) > 0
                return (
                  <Fragment key={f.cliente_id}>
                  <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <button
                        onClick={() => navigate(`/clientes/${f.cliente_id}`)}
                        title="Abrir la ficha del cliente (desde ahí se regenera)"
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          fontFamily: 'var(--font-mono)', fontSize: 'inherit',
                          color: 'var(--color-text)', cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationColor: 'var(--color-border-2)',
                          textUnderlineOffset: '3px',
                        }}
                      >
                        {f.cliente_id}
                      </button>
                      {/* Un tenant de baja sigue teniendo dashboard servido, así que
                          tiene que aparecer; pero su plantilla vieja no es una tarea
                          pendiente, y sin esta marca inflaría la lista de trabajo. */}
                      {f.activo === false && (
                        <span style={{ marginLeft: '6px', fontSize: 'var(--text-xs)',
                                       color: 'var(--color-text-3)' }}>(de baja)</span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--color-text-2)' }}>{f.tier || '—'}</td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap',
                                 color: f.al_dia ? 'var(--color-ok)' : 'var(--color-error)' }}>
                      {f.al_dia ? '● al día' : '● le falta'}
                    </td>
                    <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)',
                                 whiteSpace: 'nowrap', color: 'var(--color-text-2)' }}>
                      {_fecha(f.generado_en)}
                    </td>
                    {/* El total dice que algo pasa; no dice qué. Un clic lo desglosa
                        por constante horneada — que es de donde sale el peso de un
                        dashboard— sin salir de la pantalla. */}
                    <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)',
                                 whiteSpace: 'nowrap', color: 'var(--color-text-2)' }}>
                      {!f.kb ? '—' : f.peso ? (
                        <button
                          onClick={() => setPeso(p => (p === f.cliente_id ? '' : f.cliente_id))}
                          title="Ver de qué está hecho este peso"
                          style={{
                            background: 'none', border: 'none', padding: 0,
                            fontFamily: 'inherit', fontSize: 'inherit', cursor: 'pointer',
                            color: muerto ? 'var(--color-warn)' : 'var(--color-text-2)',
                            textDecoration: 'underline',
                            textDecorationColor: 'var(--color-border-2)',
                            textUnderlineOffset: '3px',
                          }}
                        >
                          {f.kb} KB{muerto ? ' ⚠' : ''}
                        </button>
                      ) : `${f.kb} KB`}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--color-text-2)' }}>
                      {faltan.length === 0 ? (
                        <span style={{ color: 'var(--color-text-3)' }}>
                          nada
                          {/* «No aplica» no es «cumplido»: si el plan del tenant no
                              trae un módulo, sus marcadores no se le reclaman — pero
                              esconderlo haría creer que se comprobaron. */}
                          {(f.no_aplican?.length || 0) > 0 &&
                            ` · ${f.no_aplican.length} no aplican a su plan`}
                        </span>
                      ) : (
                        <>
                          {/* La lista entera puede tener quince entradas y convertir la
                              tabla en un muro. Se enseña la primera —que ya dice de qué
                              va— y el resto bajo demanda. */}
                          <div>{faltan[0]}</div>
                          {faltan.length > 1 && !desplegado && (
                            <button
                              onClick={() => setAbierto(f.cliente_id)}
                              style={{
                                background: 'none', border: 'none', padding: 0,
                                fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                                cursor: 'pointer', textDecoration: 'underline',
                              }}
                            >
                              y {faltan.length - 1} más
                            </button>
                          )}
                          {desplegado && (
                            <ul style={{ margin: '4px 0 0', paddingLeft: 'var(--space-4)' }}>
                              {faltan.slice(1).map((x, i) => (
                                <li key={i} style={{ fontSize: 'var(--text-xs)' }}>{x}</li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {peso === f.cliente_id && f.peso && (
                    <tr>
                      <td colSpan={6} style={{ padding: '2px 8px 12px 8px',
                                               background: 'var(--color-surface-2)' }}>
                        <DesglosePeso peso={f.peso} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}


/**
 * De qué está hecho el peso de un dashboard.
 *
 * Nace del encargo de Manuel (05-09-2026): *«revisa el excesivo peso de
 * solofresco»* —16.351 KB frente a los 291 de la plantilla vacía—. La pregunta
 * no se puede contestar desde fuera: el peso vive en las constantes que el
 * generador hornea con los datos de ESE tenant, y cuáles son las gordas depende
 * de su catálogo, su mercado y sus órdenes. Así que en vez de una hipótesis, la
 * medida — que es lo que el §3 pide cuando se va a hablar de una cifra.
 */
function DesglosePeso({ peso }) {
  const filas = (peso.constantes || []).filter(c => c.kb >= 1).slice(0, 12)
  const muertas = peso.muerto?.constantes || []
  return (
    <div style={{ fontSize: 'var(--text-xs)' }}>
      <div style={{ color: 'var(--color-text-3)', marginBottom: 'var(--space-2)' }}>
        {/* «Datos» y «resto» separan lo que crece con el tenant de lo que es la
            plantilla. Un dashboard sano es casi todo plantilla. */}
        <b style={{ fontFamily: 'var(--font-mono)' }}>{peso.datos_kb} KB</b> de datos
        horneados · <span style={{ fontFamily: 'var(--font-mono)' }}>{peso.resto_kb} KB</span> de
        plantilla, CSS y JavaScript
      </div>

      {muertas.length > 0 && (
        <div style={{ color: 'var(--color-warn)', marginBottom: 'var(--space-2)' }}>
          ⚠ {peso.muerto.kb} KB en constantes RETIRADAS que este HTML todavía
          carga ({muertas.join(', ')}): las quita una regeneración. No es solo
          peso — CAT y HT_POSICION son una segunda copia del catálogo que MIPOS ya
          trae, y una copia vieja puede discrepar de la que se enseña.
        </div>
      )}

      {filas.length === 0 ? (
        <div style={{ color: 'var(--color-text-3)' }}>
          Ninguna constante llega a 1 KB: el peso es todo plantilla.
        </div>
      ) : (
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {filas.map(c => (
              <tr key={c.nombre}>
                <td style={{ padding: '2px 12px 2px 0', fontFamily: 'var(--font-mono)',
                             color: muertas.includes(c.nombre)
                               ? 'var(--color-warn)' : 'var(--color-text)' }}>
                  {c.nombre}
                </td>
                <td style={{ padding: '2px 12px 2px 0', fontFamily: 'var(--font-mono)',
                             textAlign: 'right', color: 'var(--color-text-2)' }}>
                  {c.kb} KB
                </td>
                <td style={{ padding: '2px 12px 2px 0', color: 'var(--color-text-3)' }}>
                  {c.pct} %
                </td>
                <td style={{ padding: '2px 0', width: '160px' }}>
                  <div style={{ height: '6px', borderRadius: '3px',
                                background: 'var(--color-border)' }}>
                    <div style={{
                      height: '100%', borderRadius: '3px',
                      width: `${Math.min(100, c.pct)}%`,
                      background: muertas.includes(c.nombre)
                        ? 'var(--color-warn)' : 'var(--color-accent)',
                    }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
